// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';
import emailPreviewApi from './emailPreviewApi';
import type {
  EmailPolicyPreview,
  EmailPreviewResponse,
  EmailRecipientPreview,
} from '../types/emailPreview';
import type {
  InvitationStatusResponse,
  InvitationSummary,
  SendInvitationRequest,
  BulkInvitationRequest,
  BulkInvitationResponse,
  ProfileLink,
  InvitationPreviewResponse,
  SendInvitationWithPreviewRequest,
  SendInvitationWithPreviewResponse,
  SkipInvitationRequest,
  SkipInvitationResponse,
  BatchSendInvitationsRequest,
  BatchSendInvitationsResponse,
  BatchSkipInvitationsRequest,
  BatchSkipInvitationsResponse,
  RateLimitStatus,
  ResendInvitationWithCustomizationRequest,
  ResendInvitationWithCustomizationResponse,
  ResendClientInfo,
  ResendRateLimitWarning,
  EmailTemplate,
  ClientPolicyInfo,
  ClientInvitationPreview,
} from '../types/invitation';

/** Convert an EmailRecipientPreview row into the ClientInvitationPreview
 * shape the existing invitation UI pages expect. */
function toClientPreview(r: EmailRecipientPreview): ClientInvitationPreview {
  return {
    clientId: r.id,
    clientName: r.name,
    email: r.email,
    phone: r.phone ?? undefined,
    isPrimary: r.isPrimary ?? false,
    invitationStatus: (r.status as ClientInvitationPreview['invitationStatus']) ?? 'DRAFT',
    lastInvitedAt: r.lastContactedAt ?? undefined,
    invitationCount: r.contactCount,
    skipReason: (r.skipReason as ClientInvitationPreview['skipReason']) ?? undefined,
    skipNotes: r.skipNotes ?? undefined,
  };
}

function toClientPolicyInfo(p: EmailPolicyPreview): ClientPolicyInfo {
  return {
    policyId: p.policyId,
    policyNumber: p.policyNumber ?? '',
    policyTypeName: p.policyTypeName ?? undefined,
    providerName: p.providerName ?? undefined,
    providerLogoUrl: p.providerLogoUrl ?? undefined,
    ownershipType: p.ownershipType ?? undefined,
    ownerCount: p.ownerCount ?? undefined,
    signatoryCount: p.signatoryCount ?? undefined,
    updatedAt: p.updatedAt ?? undefined,
    signatoryStatus: p.signatoryStatus ?? 'DRAFT',
    relationshipType: p.relationshipType ?? undefined,
    providerRejected: p.providerRejected,
  };
}

/** Adapt the unified preview response to the legacy InvitationPreviewResponse
 * shape the wizard + resend pages were built against. */
function adaptToInvitationPreview(
  profileId: string,
  preview: EmailPreviewResponse
): InvitationPreviewResponse {
  return {
    profileId,
    profileName: '',
    clients: preview.recipients.map(toClientPreview),
    defaultTemplate: {
      subject: preview.subject,
      bodyHtml: preview.renderedPreviews[0]?.bodyHtml ?? preview.editableBodyHtml,
      editableBodyHtml: preview.editableBodyHtml,
    },
    policies: preview.policies.map(toClientPolicyInfo),
  };
}

export const invitationApi = {
  /**
   * Send invitation to a single client
   */
  sendInvitation: async (profileId: string, clientId: string): Promise<{ success: boolean; message: string }> => {
    const request: SendInvitationRequest = { profileId, clientId };
    const response = await api.post('/invitations/clients/send', request);
    return response.data;
  },

  /**
   * Send invitations to all clients in a profile
   */
  sendBulkInvitations: async (profileId: string, skipActive?: boolean): Promise<BulkInvitationResponse> => {
    const request: BulkInvitationRequest = { profileId, skipActive };
    const response = await api.post('/invitations/clients/send-all', request);
    return response.data;
  },

  /**
   * Resend invitation to a client
   */
  resendInvitation: async (profileId: string, clientId: string): Promise<{ success: boolean; message: string }> => {
    const request: SendInvitationRequest = { profileId, clientId };
    const response = await api.post('/invitations/clients/resend', request);
    return response.data;
  },

  /**
   * Get invitation status for a client
   */
  getInvitationStatus: async (clientId: string): Promise<InvitationStatusResponse> => {
    const response = await api.get(`/invitations/clients/${clientId}/status`);
    return response.data;
  },

  /**
   * Get invitation summary for a profile
   */
  getInvitationSummary: async (profileId: string): Promise<InvitationSummary> => {
    const response = await api.get(`/invitations/clients/profile/${profileId}/summary`);
    return response.data;
  },

  /**
   * Generate profile link for client registration
   */
  generateProfileLink: async (profileId: string): Promise<ProfileLink> => {
    const response = await api.get(`/invitations/clients/profile/${profileId}/link`);
    return response.data;
  },

  // ========================================
  // Invitation Preview APIs (Edit Email Feature)
  // ========================================

  /**
   * Get invitation preview for a profile with all clients and default template.
   *
   * Delegates to the unified {@code POST /api/email-preview} endpoint and
   * adapts the response into the legacy {@link InvitationPreviewResponse}
   * shape so the wizard + resend pages don't have to change.
   */
  getInvitationPreview: async (profileId: string): Promise<InvitationPreviewResponse> => {
    const preview = await emailPreviewApi.preview({
      kind: 'CLIENT_LOA_INVITATION',
      profileId,
    });
    return adaptToInvitationPreview(profileId, preview);
  },

  /**
   * Send invitation to a single client with optional custom email
   */
  sendInvitationWithPreview: async (
    profileId: string,
    clientId: string,
    request: SendInvitationWithPreviewRequest
  ): Promise<SendInvitationWithPreviewResponse> => {
    const response = await api.post(
      `/invitations/clients/profile/${profileId}/clients/${clientId}/send-invitation`,
      request
    );
    return response.data;
  },

  /**
   * Skip invitation for a single client
   */
  skipInvitation: async (
    profileId: string,
    clientId: string,
    request: SkipInvitationRequest
  ): Promise<SkipInvitationResponse> => {
    const response = await api.post(
      `/invitations/clients/profile/${profileId}/clients/${clientId}/skip-invitation`,
      request
    );
    return response.data;
  },

  /**
   * Send invitations to multiple clients in batch
   */
  sendInvitationsBatch: async (
    profileId: string,
    request: BatchSendInvitationsRequest
  ): Promise<BatchSendInvitationsResponse> => {
    const response = await api.post(
      `/invitations/clients/profile/${profileId}/batch-send`,
      request
    );
    return response.data;
  },

  /**
   * Skip invitations for multiple clients in batch
   */
  skipInvitationsBatch: async (
    profileId: string,
    request: BatchSkipInvitationsRequest
  ): Promise<BatchSkipInvitationsResponse> => {
    const response = await api.post(
      `/invitations/clients/profile/${profileId}/batch-skip`,
      request
    );
    return response.data;
  },

  /**
   * Get current rate limit status for the advisor
   */
  getRateLimitStatus: async (): Promise<RateLimitStatus> => {
    const response = await api.get('/invitations/clients/rate-limit-status');
    return response.data;
  },

  // ========================================
  // Resend Invitation APIs (Customizable Email)
  // ========================================

  /**
   * Get client info and default template for resend invitation page
   */
  getResendInvitationData: async (
    profileId: string,
    clientId: string
  ): Promise<{
    clientInfo: ResendClientInfo;
    defaultTemplate: EmailTemplate;
    rateLimitWarning: ResendRateLimitWarning | null;
    policies: ClientPolicyInfo[];
  }> => {
    const preview = await emailPreviewApi.preview({
      kind: 'CLIENT_LOA_INVITATION',
      profileId,
      clientId,
    });
    const recipient = preview.recipients[0];
    const clientInfo: ResendClientInfo = {
      clientId,
      clientName: recipient?.name ?? '',
      clientEmail: recipient?.email ?? '',
      profileId,
      profileName: '',
      status: recipient?.status ?? 'DRAFT',
      lastInvitedAt: recipient?.lastContactedAt ?? undefined,
    };
    const rateLimitWarning: ResendRateLimitWarning | null = preview.rateLimit
      ? {
          code: preview.rateLimit.code ?? '',
          message: preview.rateLimit.message ?? '',
          lastSentAt: preview.rateLimit.lastSentAt ?? '',
        }
      : null;
    return {
      clientInfo,
      defaultTemplate: {
        subject: preview.subject,
        bodyHtml: preview.renderedPreviews[0]?.bodyHtml ?? preview.editableBodyHtml,
        editableBodyHtml: preview.editableBodyHtml,
      },
      rateLimitWarning,
      policies: preview.policies.map(toClientPolicyInfo),
    };
  },

  /**
   * Resend invitation with optional customization
   */
  resendInvitationWithCustomization: async (
    profileId: string,
    clientId: string,
    request?: ResendInvitationWithCustomizationRequest
  ): Promise<ResendInvitationWithCustomizationResponse> => {
    const response = await api.post(
      `/invitations/clients/profile/${profileId}/clients/${clientId}/resend`,
      request || { forceResend: true }
    );
    return response.data;
  },

  // ========================================
  // Add-Policies Invitation APIs
  // ========================================

  /**
   * Get add-policies invitation preview for a profile
   * Uses the client-add-policies-invitation template with "Add Policy" CTA
   */
  getAddPoliciesInvitationPreview: async (profileId: string): Promise<InvitationPreviewResponse> => {
    const preview = await emailPreviewApi.preview({
      kind: 'CLIENT_ADD_POLICIES_INVITATION',
      profileId,
    });
    return adaptToInvitationPreview(profileId, preview);
  },

  /**
   * Send add-policies invitation to a single client with optional customization
   */
  sendAddPoliciesInvitation: async (
    profileId: string,
    clientId: string,
    request?: { customSubject?: string; customBody?: string; forceResend?: boolean }
  ): Promise<SendInvitationWithPreviewResponse> => {
    const response = await api.post(
      `/invitations/clients/profile/${profileId}/clients/${clientId}/send-add-policies-invitation`,
      request || {}
    );
    return response.data;
  },

  /**
   * Send one consolidated invitation email covering every Profile Request
   * id in {@code requestIds}. Used by the advisor "New Request" wizard
   * after it creates N requests in a single batch so the client receives
   * one email rather than one per request type.
   */
  sendRequestsInvitation: async (
    profileId: string,
    clientId: string,
    request: {
      customSubject?: string;
      customBody?: string;
      requestIds: string[];
    }
  ): Promise<SendInvitationWithPreviewResponse> => {
    const response = await api.post(
      `/invitations/clients/profile/${profileId}/clients/${clientId}/send-requests-invitation`,
      request
    );
    return response.data;
  },
};
